export default {
  appName: 'NFC Share',
  company: 'Shanghai Qinfu Information Technology Co., Ltd.',
  nav: {
    home: 'Home',
    privacy: 'Privacy Policy',
    support: 'Technical Support',
    contact: 'Contact Us'
  },
  home: {
    title: 'NFC Share',
    subtitle: 'Powerful NFC Tag Read/Write Tool',
    description: 'Easily read and write NFC tags, supporting text, URLs, music links, audio, video, images and more',
    featuresTitle: 'Core Features',
    featuresSubtitle: 'Powerful NFC read and write functions to meet all your needs',
    features: {
      uidReader: {
        title: 'UID Reader',
        subtitle: 'Free',
        description: 'Quickly read NFC tag UIDs, export data, completely free to use',
        items: {
          item1: 'Fast NFC tag UID reading',
          item2: 'Data export support',
          item3: 'Completely free to use'
        }
      },
      freeWrite: {
        title: 'Free Write Features',
        subtitle: 'Easy to Use',
        description: 'Support for text, URLs, music links and other basic write functions',
        items: {
          item1: 'Text, URLs, music links',
          item2: 'Simple and easy interface',
          item3: 'Free to use'
        }
      },
      advancedWrite: {
        title: 'Advanced Write Features',
        subtitle: 'Multimedia Support',
        description: 'Support for writing audio, video, images and other multimedia content',
        items: {
          item1: 'Audio, video, images',
          item2: 'Multimedia support',
          item3: 'High quality content writing'
        }
      }
    },
    download: 'Download Now',
    learnMore: 'Learn More',
    comingSoon: 'Coming Soon',
    envPills: ['iOS 16.6 or later', 'iPhone 7 and later', 'NDEF-format tags supported'],
    showcase: {
      title: 'Real Screenshots',
      subtitle: 'What you see is what you get',
      items: [
        { title: 'Multimedia Write', desc: 'Photo, audio, video, text and custom chips' },
        { title: 'Basic Write', desc: 'Seven data types: text, URL, music links and more' },
        { title: 'UID Reader', desc: 'Batch scanning with exportable records' },
        { title: 'Tag Plans', desc: 'Purchase by type, quota arrives instantly' }
      ]
    },
    steps: {
      title: 'Write in Four Steps',
      subtitle: 'No expertise needed — just pick up your phone',
      items: [
        { title: 'Choose Tag Type', desc: 'Photo, audio, video, text chips and more' },
        { title: 'Add Content', desc: 'Upload files or enter text with live preview' },
        { title: 'Tap to Write', desc: 'Hold the tag near your iPhone for a few seconds' },
        { title: 'Tap to Access', desc: 'Anyone can view the content with a simple tap' }
      ]
    },
    scenes: {
      title: 'Use Cases',
      subtitle: 'A tiny tag holds a big world',
      items: [
        { title: 'Personal Items', desc: 'Tag your belongings, check info anytime' },
        { title: 'Smart Business Card', desc: 'Share contacts and works with a tap' },
        { title: 'Product Display', desc: 'Customers tap the tag to see details' },
        { title: 'Marketing', desc: 'Add tags to posters and booths for traffic' }
      ]
    }
  },
  privacy: {
    title: 'User Privacy Policy',
    effectiveDate: 'September 17, 2025',
    lastUpdated: 'Last Updated',
    intro: 'NFC Share (operated by Shanghai Qinfu Information Technology Co., Ltd., hereinafter referred to as "we" or "this app") attaches great importance to your privacy protection. This privacy policy aims to explain to you how we collect, use, store and protect your personal information. Before using this app, please read this privacy policy carefully.',
    sections: {
      infoCollection: {
        title: 'Information Collection Scope',
        account: {
          title: 'Account Information',
          content: 'We collect the following account-related information to provide you with account registration, login and management services:',
          items: ['Email address: for account registration, login, receiving verification codes and password reset', 'Password: encrypted and stored for account security verification', 'Username: to display your identity in the app', 'Phone number (optional): for account verification and contact', 'Avatar link: to personalize your account information display', 'User ID: a unique identifier automatically generated by the system']
        },
        thirdParty: {
          title: 'Third-Party Login Information',
          content: 'When you choose to sign in with Apple, we collect:',
          items: ['Apple ID credentials: for identity verification', 'Apple Sign-In Token: to maintain your login status']
        },
        nfc: {
          title: 'NFC Functionality Data',
          content: 'When you use NFC features, we may collect:',
          items: ['NFC tag UID: to identify and record the NFC tags you scan', 'NFC write content: including text, URLs, music links, audio, video, images and other content you choose to write to tags', 'Scan records: including scan time, tag type, connection quality and other technical information', 'Export data: you can choose to export scan records as CSV files']
        },
        files: {
          title: 'File Upload Data',
          content: 'When you use advanced write features to upload multimedia files:',
          items: ['File content: audio, video, image files you upload', 'File metadata: file size, file type, file name and other information', 'Upload records: file upload status and results'],
          note: 'Note: Files are stored and transmitted through Tencent Cloud Object Storage (COS) service.'
        },
        payment: {
          title: 'Payment Information',
          content: 'When you use payment features, we collect:',
          items: ['Order information: order ID, payment amount, payment status', 'Payment platform: payment information returned by third-party payment platforms such as Alipay', 'Payment results: to confirm payment status and complete orders'],
          note: 'Note: We do not collect or store your bank card number, payment password and other sensitive payment information, which is directly processed by third-party payment platforms (Alipay).'
        },
        device: {
          title: 'Device Information',
          content: 'To provide better service experience, we may collect:',
          items: ['Device identifier: for device recognition and service optimization', 'App version information: for version updates and compatibility handling', 'System language settings: to provide localized services']
        },
        usage: {
          title: 'Usage Data',
          content: 'We may collect the following usage data:',
          items: ['Feature usage records: frequency and manner of your use of various features', 'Error logs: to improve app stability and performance', 'Network request information: for service optimization and troubleshooting']
        }
      },
      infoUsage: {
        title: 'Information Usage Purpose',
        content: 'We collect your personal information for the following purposes:',
        items: ['Service provision: provide core features such as NFC tag read/write, data management, account management', 'Account management: account-related services such as registration, login, identity verification, password reset', 'Payment processing: process order payment, query payment status, complete transactions', 'File storage: store multimedia files you upload for access through NFC tags', 'Service improvement: analyze usage to optimize app features and performance', 'Security protection: detect and prevent security threats and fraudulent behavior', 'Customer support: respond to your inquiries, feedback and complaints', 'Compliance requirements: comply with legal and regulatory requirements, cooperate with regulatory investigations']
      },
      infoStorage: {
        title: 'Information Storage and Protection',
        local: {
          title: 'Local Storage',
          content: 'The following information is stored locally on your device:',
          items: ['Authentication Token: stored in UserDefaults to maintain login status', 'App settings: language preferences, theme settings and other local configurations', 'Scan records: your local NFC scan data (optional export)']
        },
        server: {
          title: 'Server Storage',
          content: 'The following information is stored on the server:',
          items: ['Account information: email, username, encrypted password, etc.', 'NFC card data: NFC card information you create', 'Uploaded files: multimedia files stored through COS service', 'Order records: payment and order-related data']
        },
        security: {
          title: 'Data Security Measures',
          content: 'We take the following measures to protect your information security:',
          items: ['Encrypted transmission: use HTTPS protocol to encrypt data transmission', 'Password encryption: user passwords are encrypted and stored', 'Access control: limit employee access to user data', 'Security audit: conduct regular security audits and vulnerability detection', 'Data backup: regularly back up important data to prevent data loss']
        }
      },
      thirdPartyServices: {
        title: 'Third-Party Services',
        items: [
          { name: 'Tencent Cloud Object Storage (COS)', purpose: 'Store audio, video, image files you upload', data: 'Your uploaded file content', policy: 'https://cloud.tencent.com/document/product/436/7758' },
          { name: 'Alipay', purpose: 'Process payment transactions', data: 'Order information, payment amount, payment status', policy: 'https://render.alipay.com/p/c/180020270001236236' },
          { name: 'Apple Sign In', purpose: 'Provide Apple account login functionality', data: 'Apple ID credentials, Sign-In Token', policy: 'https://www.apple.com/privacy/' },
          { name: 'App Store', purpose: 'App distribution and update checking', data: 'App version information', policy: 'https://www.apple.com/privacy/' }
        ]
      },
      infoSharing: {
        title: 'Information Sharing and Disclosure',
        noSale: 'We Will Not Sell Your Personal Information',
        noSaleContent: 'We will not sell, trade or rent your personal information to third parties.',
        shareTitle: 'Information May Be Shared in the Following Situations',
        shareItems: ['Service providers: share necessary information with third parties that provide services to us (such as cloud storage, payment processing)', 'Legal requirements: in accordance with laws and regulations, court judgments or government requirements', 'Security protection: to protect our rights, property or user safety', 'Business transfer: in case of merger, acquisition or asset transfer (we will notify you in advance)']
      },
      userRights: {
        title: 'Your Rights',
        content: 'You have the following rights regarding personal information:',
        items: ['Access right: view your personal information we collect', 'Correction right: correct inaccurate or incomplete personal information', 'Deletion right: request deletion of your personal information (subject to legal requirements)', 'Withdraw consent: withdraw consent to personal information processing', 'Data export: export your personal data (NFC scan records, etc.)', 'Account cancellation: cancel account and delete related data'],
        contact: 'To exercise the above rights, please contact us through:',
        contactMethods: ['Email: support@qinfu-cloud.cn', 'In-app: Settings - Technical Support']
      },
      childrenPrivacy: {
        title: 'Children\'s Privacy Protection',
        content: 'This app is not directed to children under 13 years of age (or the age specified by law in your region). We do not knowingly collect personal information from children. If we discover that we have collected children\'s information, we will delete the relevant data immediately.'
      },
      dataRetention: {
        title: 'Data Retention',
        content: 'We retain your personal information only for the period necessary to achieve the purposes described in this privacy policy:',
        items: ['Account information: during your account validity period and for a period of time after cancellation (in compliance with legal requirements)', 'NFC data: retained until you delete it or after account cancellation', 'Payment records: retained in accordance with legal and regulatory requirements (usually 5-7 years)', 'Log data: usually retained for 90 days']
      },
      international: {
        title: 'International Data Transfer',
        content: 'Your personal information may be transferred to places outside your country/region for processing and storage. We ensure that appropriate security measures are taken to protect your data.'
      },
      policyUpdate: {
        title: 'Privacy Policy Updates',
        content: 'We may update this privacy policy from time to time. In case of significant changes, we will notify you in the app. Continued use of this app indicates your acceptance of the updated privacy policy.'
      },
      contactUs: {
        title: 'Contact Us',
        content: 'If you have any questions, comments or complaints about this privacy policy, please contact us through:',
        items: ['Company: Shanghai Qinfu Information Technology Co., Ltd.', 'App Name: NFC Share', 'Contact Email: support@qinfu-cloud.cn', 'In-app Support: Settings - Technical Support']
      },
      law: {
        title: 'Applicable Law',
        content: 'The formulation, execution and interpretation of this privacy policy are subject to the laws of the People\'s Republic of China. In case of disputes, they should be resolved through friendly negotiation; if negotiation fails, either party may file a lawsuit with a people\'s court with jurisdiction.'
      },
      notice: {
        title: 'Important Notice',
        content: 'By using this app, you indicate that you have read, understood and agreed to all the contents of this privacy policy. If you do not agree with any terms of this agreement, please stop using this app.'
      }
    }
  },
  support: {
    title: 'Technical Support',
    content: 'If you encounter any issues while using the app, please contact us through the following methods:',
    email: 'Email',
    emailDescription: 'Contact us via email',
    sendEmail: 'Send Email',
    faq: 'FAQ',
    faqDescription: 'View frequently asked questions',
    viewFAQ: 'View FAQ',
    faqTitle: 'Frequently Asked Questions',
    faqItems: {
      read: {
        question: 'How to read NFC tags?',
        answer: 'After opening the app, bring your device close to the NFC tag, and the app will automatically detect and read the tag information.'
      },
      types: {
        question: 'What types of NFC tags are supported?',
        answer: 'We support NFC tags that comply with ISO14443 and ISO15693 protocols.'
      },
      pricing: {
        question: 'Do advanced features require payment?',
        answer: 'Basic features (UID reading, text writing) are completely free. Advanced features (multimedia writing) require upgrading to the premium version.'
      }
    },
    audioGuide: {
      title: 'How to Write Audio Files',
      step1: {
        title: 'Step 1: Select Audio File',
        description: 'Select an audio file on your device and tap the Share button'
      },
      step2: {
        title: 'Step 2: Save to Files',
        description: 'Choose "Save to Files" option from the share menu'
      },
      step3: {
        title: 'Step 3: Upload in App',
        description: 'Open NFC Share app, select the saved file and upload'
      }
    },
    videoGuide: {
      title: 'Video Tutorials',
      description: 'Watch our video tutorials to learn more tips and tricks',
      bilibili: {
        title: 'Bilibili Tutorial',
        watch: 'Watch Video'
      },
      youtube: {
        title: 'YouTube Tutorial',
        watch: 'Watch Video'
      }
    },
    completeGuide: {
      title: 'Complete User Guide',
      description: 'Complete user guide covering all features and usage methods',
      bilibili: {
        title: 'Bilibili Complete Guide',
        watch: 'Watch Complete Guide'
      },
      stepsTitle: 'Usage Steps'
    }
  },
  contact: {
    title: 'Contact Us',
    subtitle: 'We\'d love to hear from you',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    wechat: 'WeChat Official',
    scanToFollow: 'Scan to Follow'
  },
  footer: {
    copyright: 'Copyright',
    allRightsReserved: 'All Rights Reserved',
    contactUs: 'Contact Us',
    privacy: 'Privacy Policy',
    terms: 'Terms of Use',
    quickLinks: 'Quick Links',
    wechat: 'WeChat Official',
    scanQR: 'Scan QR Code'
  }
}

